import { memo, ReactElement, useState } from 'react';
import { Box, Col, Row } from 'src/design-system/box/Box';
import { Font } from 'src/design-system/text/font';
import styles from './transport-map.module.scss';
import dynamic from 'next/dynamic';
import Image from 'next/image';
import { TransportTypesFilters } from '@utmb/types/transport-map';
import { PoiAccessTypeEnum } from '@utmb/types/enum/PoiAccessTypeEnum';
import PoiAccessView from '@utmb/types/view/PoiAccessView';
import SliceAccessMapView from '@utmb/types/view/SliceAccessMapView';
import { useTranslation } from 'next-i18next';
import { useGlobal } from '@utmb/contexts/GlobalContext';
import { Slice } from '@utmb/types/slice-handler';
import { expandBoundsByKm, LatLngTuple } from '../../../design-system/map/bounds';

const Map = dynamic(() => import('../../../design-system/map/Map'), {
    ssr: false,
});

const TransportMapMarkers = dynamic(() => import('./TransportMapMarkers'), {
    ssr: false,
});

type InitialFiltersProps = {
    [K in PoiAccessTypeEnum]: boolean;
};

// noinspection JSUnusedGlobalSymbols
export const TransportMap = memo((props: Slice<SliceAccessMapView>): ReactElement => {
    const { id, poisAccess: transports } = props;
    const { t } = useTranslation();

    const initialFilters: TransportTypesFilters = {
        event: true,
        boat: true,
        airport: true,
        busStation: true,
        parking: true,
        touristOffice: true,
        trainStation: true,
        funicularStation: true,
        shuttleStation: true,
        subway: true,
        taxi: true,
    };

    const [filter, setFilter] = useState<InitialFiltersProps>(initialFilters);

    const updateFilter = (type: PoiAccessTypeEnum): void => {
        const typeNewState = { [type]: !filter[type] };

        setFilter({
            ...filter,
            ...typeNewState,
        });
    };

    const types = [...new Set(transports.map(({ type }: PoiAccessView) => type))];

    const {
        global: {
            event: { lng, lat, name, primaryColorIsDark, secondaryColorIsDark },
        },
    } = useGlobal();

    const markers = [
        { info: name, lat, lng, name, type: PoiAccessTypeEnum.EVENT } as PoiAccessView,
        ...transports.filter(({ type }: PoiAccessView) => filter[type]),
    ];

    // maxBounds is computed from the unfiltered transport set so panning
    // stays consistent when the user toggles category filters.
    const allMarkerCoords: LatLngTuple[] = [[lat, lng], ...transports.map((t: PoiAccessView): LatLngTuple => [t.lat, t.lng])];
    const maxBounds = expandBoundsByKm(allMarkerCoords, 50) ?? undefined;

    return (
        <>
            {transports && transports.length !== 0 && (
                <Col id={id} className={styles.tm_container}>
                    <Box className={styles.tm_map_container}>
                        <div className={styles.tm_map}>
                            <Map
                                defaultLayer="osm"
                                minZoom={6}
                                maxBounds={maxBounds}
                                className={`${secondaryColorIsDark ? '' : 'lightbg'} ${primaryColorIsDark ? '' : 'lightprimbg'}`}
                            >
                                <TransportMapMarkers markers={markers} />
                            </Map>
                            <Row className={styles.tm_map_filters}>
                                <div className="utmb_container_v2">
                                    {transports && (
                                        <Row className={styles.tm_map_filters_items}>
                                            {types.map((type) => {
                                                const filterItemCSS = [styles.tm_map_filter, filter[type] && styles.tm_map_filter_active]
                                                    .filter(Boolean)
                                                    .join(' ');
                                                const callback = (): void => updateFilter(type);
                                                return (
                                                    <a key={type} className={filterItemCSS} onClick={callback} onKeyPress={callback} role="button" tabIndex={0}>
                                                        <Font.OxaniumRegular mobile="14" className={styles.tm_map_filter_label}>
                                                            {t(`poiAccess.types.${type.valueOf()}`)}
                                                        </Font.OxaniumRegular>
                                                        <Image
                                                            width="30"
                                                            height="30"
                                                            className={styles.tm_map_filter_image}
                                                            src={`/map-markers/icon-${type}.svg`}
                                                        />
                                                    </a>
                                                );
                                            })}
                                        </Row>
                                    )}
                                </div>
                            </Row>
                        </div>
                    </Box>
                </Col>
            )}
        </>
    );
});
